'use client'

import React, { useEffect, useState } from 'react';
import CustomButton from '../individual-components/button';
import CustomMenu from './custom-menu';
import ChevronDownIcon from './chevron-down';
import NextImage from '../NextImage';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { usePathname } from 'next/navigation';
import ResourcesDropdown from './resources-dropdown';
import { getBlogsListAPI, getCaseStudyListAPI, getEBooksListAPI, getPressListAPI } from '@/api-config/get';
import { useDispatch, useSelector } from 'react-redux';
import HyqooLogo from '../individual-components/hyqoo-logo/page';

const RESOURCES_OPTIONS = [
    { label: 'Blogs & Articles', href: '/blog', target: '' },
    { label: 'Case Studies', href: '/case-studies', target: '' },
    { label: 'Press', href: '/press', target: '' },
    { label: 'eBook & Market Reports', href: '/resource-center', target: '' }
];

const ACCOUNT_OPTIONS = [
    { label: 'Create an Account', href: 'https://talent.hyqoo.com/create-account', target: '_blank' },
    { label: 'Login', href: 'https://talent.hyqoo.com', target: '_blank' }
];

const navLinks = [
    { href: "/why-hyqoo", label: "Why Hyqoo" },
    { href: "/find-jobs", label: "Find Jobs" },
    { href: "/about", label: "About Us" },
    { href: "/circles", label: "Community" },
];

const activePageClass = 'text-gray-900 font-semibold md:after:opacity-0 after:absolute after:-bottom-5 after:left-0 after:w-full after:h-[2px] after:bg-[#eb532e]';

const getResourcesClass = (activePage, mobile) => {
    const isActive = ['/blog', '/case-studies', '/press'].some(path => activePage.startsWith(path));
    if (mobile && isActive) return activePageClass;
    return mobile ? "" : isActive ? activePageClass : "text-[#6B7280] hover:text-black hover:underline transition-all duration-200 relative py-6";
};

const Header = () => {
    const [showResourcesOptions, setShowResourcesOptions] = useState(false);
    const [showAccountOptions, setShowAccountOptions] = useState(false);  //false
    const [pageData, setPageData] = useState({ page: 1, pageSize: 10 });
    const [loader, setLoader] = useState(false);
    const dispatch = useDispatch();
    const activePage = usePathname();

    const blogsSlice = useSelector((state) => state?.blogsSlice);
    const caseStudySlice = useSelector((state) => state?.caseStudySlice);
    const pressSlice = useSelector((state) => state?.pressSlice);
    const eBooksSlice = useSelector((state) => state?.eBooksSlice);

    const pinnedBlogsList = blogsSlice?.pinned?.data;
    const pinnedCaseStudyList = caseStudySlice?.pinned?.data;
    const pinnedPressList = pressSlice?.pinned?.data;

    const pinnedBlogData = pinnedBlogsList?.length ? pinnedBlogsList[0] : null;
    const pinnedCaseStudyData = pinnedCaseStudyList?.length ? pinnedCaseStudyList[0] : null;
    const pinnedPressData = pinnedPressList?.length ? pinnedPressList[0] : null;

    useEffect(() => {
        dispatch(getBlogsListAPI(setLoader, { ...pageData, category: "pinned" }));
        dispatch(getCaseStudyListAPI(setLoader, { ...pageData, category: "pinned" }));
        dispatch(getPressListAPI(setLoader, { ...pageData, category: "pinned" }));
        dispatch(getEBooksListAPI(setLoader, { ...pageData }));
    }, []);

    const resourcesClass = getResourcesClass(activePage);

    return (
        <header className="fixed top-0 left-0 z-50 w-full">
            <div className="relative py-3 bg-white bg-opacity-90 min-h-[98px] max-h-[98px] md:min-h-[70px] flex items-center">
                <div className="w-full px-4 mx-auto max-w-7xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center custom-gap-8 transition-all">
                            <div className="logo-nav">
                                <HyqooLogo />
                            </div>

                            <div className="flex md:hidden items-center custom-gap-8 transition-all">
                                {navLinks.map(({ href, label }) => (
                                    <HeaderLink key={href} href={href} label={label} />
                                ))}

                                <div className='relative'>
                                    <button
                                        aria-label='resources'
                                        className={resourcesClass + " flex items-center gap-3 px-1 py-6 after:bottom-[2px] after:-left-2"}
                                        onMouseEnter={() => setShowResourcesOptions(true)}
                                        onMouseLeave={() => setShowResourcesOptions(false)}
                                    >
                                        Resources
                                        <ChevronDownIcon
                                            className={`${showResourcesOptions ? "rotate-0" : "-rotate-90"} transition-all duration-300`}
                                        />
                                        <ResourcesDropdown
                                            loader={loader}
                                            open={showResourcesOptions}
                                            onClick={() => setShowResourcesOptions(false)}
                                            cardData={pinnedBlogData}
                                            caseCardData={pinnedCaseStudyData}
                                            pressCardData={pinnedPressData}
                                            eBooksData={eBooksSlice?.data?.find(e => e?.bookId)}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className='flex items-center gap-3 md:hidden'>
                            <CustomButton
                                href='/hiretalent'
                                label='Hire Talent'
                                variant='outlined'
                                className='max-h-10 text-black'
                            />
                            <button
                                aria-label='apply-jobs'
                                className={`relative ${showAccountOptions ? 'py-5' : ''}`}
                                onMouseEnter={() => setShowAccountOptions(true)}
                                onMouseLeave={() => setShowAccountOptions(false)}
                            >
                                <CustomButton
                                    href='https://talent.hyqoo.com/create-account'
                                    target={"_blank"}
                                    label='Apply for jobs'
                                    variant='outlined'
                                    className='max-h-10 text-black'
                                />
                                <CustomMenu
                                    open={showAccountOptions}
                                    options={ACCOUNT_OPTIONS}
                                    position={'top-20 -left-20'}
                                />
                            </button>
                        </div>

                        <MobileMenu
                            setShowResourcesOptions={setShowResourcesOptions}
                            showResourcesOptions={showResourcesOptions}
                            setShowAccountOptions={setShowAccountOptions}
                            showAccountOptions={showAccountOptions}
                        />
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;

const MobileMenu = ({ setShowResourcesOptions, showResourcesOptions, setShowAccountOptions, showAccountOptions }) => {

    const activePage = usePathname();
    const resourcesClass = getResourcesClass(activePage, true);
    const [menuOpen, setMenuOpen] = useState(false);

    const closeSubMenu = () => {
        setShowAccountOptions(false)
        setShowResourcesOptions(false)
    }

    const toggleMenu = () => {
        setMenuOpen((prev) => !prev)
        closeSubMenu()
    };
    const closeMenu = () => {
        setMenuOpen(false)
        closeSubMenu()
    };

    const toggleDropdown = (setState, submenu) => {
        setState(state => !state)
        if (submenu == 'account') {
            setShowResourcesOptions(false)
            return;
        }
        setShowAccountOptions(false);
    }

    useEffect(() => {
        if (menuOpen) {
            closeMenu()
        }
    }, [activePage])

    return (
        <div className="hidden md:flex">
            <button aria-label="toggle-menu" className="py-4 cursor-pointer" onClick={toggleMenu}>
                {menuOpen ? (
                    <NextImage
                        width="14px"
                        height="14px"
                        alt="close icon"
                        src="https://hyqoo.b-cdn.net/WebsiteAssets/images/close.svg"
                        className='rotate-45'
                    />
                ) : (
                    <NextImage
                        width="20px"
                        height="20px"
                        alt="menu icon"
                        src="https://hyqoo.b-cdn.net/WebsiteAssets/images/menu.svg"
                    />
                )}
            </button>

            <div className={`absolute left-0 w-full bg-white transition-all duration-500 overflow-auto top-[4.375rem] ${menuOpen ? "max-h-[calc(100vh-70px)]" : "max-h-0"}`}>
                <div className="flex flex-col gap-6 px-3 pt-3">
                    {navLinks.map(({ href, label }) => (
                        <HeaderLink key={href} href={href} label={label} onClick={closeMenu} />
                    ))}
                    <button
                        aria-label="resources"
                        className={`text-[#6B7280] w-fit text-left pl-1 flex items-center gap-3 hover:text-black hover:underline transition-all duration-200 relative ${resourcesClass}`}
                        onClick={() => toggleDropdown(setShowResourcesOptions, 'resources')}
                    >
                        Resources
                        <ChevronDownIcon
                            className={`${showResourcesOptions ? "rotate-180" : ""} transition-all duration-300`}
                        />
                        {showResourcesOptions &&
                            <CustomMenu
                                onClick={closeMenu}
                                open={showResourcesOptions}
                                options={RESOURCES_OPTIONS}
                            />
                        }
                    </button>
                    <HeaderLink href="/hiretalent" label="Hire Talent" onClick={closeMenu} />
                    <button
                        aria-label="apply-jobs"
                        className="text-[#6B7280] w-fit text-left pl-1 flex items-center gap-3 hover:text-black hover:underline transition-all duration-200 relative"
                        onClick={() => toggleDropdown(setShowAccountOptions, 'account')}
                    >
                        Apply for Jobs
                        <ChevronDownIcon
                            className={`${showAccountOptions ? "rotate-180" : ""} transition-all duration-300`}
                        />
                        {showAccountOptions &&
                            <CustomMenu
                                onClick={closeMenu}
                                open={showAccountOptions}
                                options={ACCOUNT_OPTIONS}
                            />
                        }
                    </button>
                </div>
                <div className='flex w-full bg-[#F9FAFB] mt-14 justify-center items-center'>
                    <Link
                        href="/"
                        aria-label="get-in-touch"
                        className="text-[#EB532E] py-8 bg-[#F9FAFB] text-center"
                        onClick={closeMenu}
                    >
                        Looking for a new career? Get in touch
                    </Link>
                </div>
            </div>
        </div>
    );
};

MobileMenu.propTypes = {
    setShowResourcesOptions: PropTypes.func.isRequired,
    showResourcesOptions: PropTypes.bool.isRequired,
    setShowAccountOptions: PropTypes.func.isRequired,
    showAccountOptions: PropTypes.bool.isRequired,
};

const HeaderLink = ({ href, label, onClick = () => { } }) => {
    const activePage = usePathname();
    return (
        <div className='relative'>
            <Link
                href={href}
                aria-label={label.toLowerCase().replace(" ", "-")}
                className={`${activePage === href ? activePageClass : 'text-[#6B7280]'} px-1 hover:text-black hover:underline`}
                onClick={onClick}
            >
                {label}
            </Link>
        </div>
    );
};

HeaderLink.propTypes = {
    href: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
};
